import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User, Phone, Mail, Calendar, FileText, MapPin, Users, AlertCircle, CheckCircle } from 'lucide-react';
import { RegistrationFormData, Tour, CustomTour } from '../../types';
import { submitRegistration } from '../../services/registrationService';

interface RegistrationFormProps {
  tours: Tour[];
  customTours?: CustomTour[];
  selectedTourId?: string;
  tourType?: 'regular' | 'custom';
  onSuccess?: (registrationId: string) => void;
  onError?: (error: string) => void;
}

const RegistrationForm: React.FC<RegistrationFormProps> = ({
  tours,
  customTours = [],
  selectedTourId,
  tourType = 'regular',
  onSuccess,
  onError
}) => {
  const [formData, setFormData] = useState<RegistrationFormData>({
    givenName: '',
    surname: '',
    phoneNumber: '',
    email: '',
    dateOfBirth: '',
    passportNumber: '',
    nationality: 'United States', // Default value since it's now required
    sex: 'male', // Default value since it's now required
    address: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
    passportIssueDate: '',
    passportExpiryDate: '',
    tourId: selectedTourId || '',
    emergencyContact: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    dietaryRequirements: '',
    medicalConditions: ''
  });

  const [countryCode, setCountryCode] = useState('+1'); // Default to US

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Update tourId when selectedTourId prop changes
  useEffect(() => {
    if (selectedTourId) {
      setFormData(prev => ({ ...prev, tourId: selectedTourId }));
    }
  }, [selectedTourId]);

  // Common country codes
  const countryCodes = [
    { code: '+1', country: 'US/Canada' },
    { code: '+44', country: 'UK' },
    { code: '+91', country: 'India' },
    { code: '+86', country: 'China' },
    { code: '+81', country: 'Japan' },
    { code: '+82', country: 'South Korea' },
    { code: '+65', country: 'Singapore' },
    { code: '+60', country: 'Malaysia' },
    { code: '+66', country: 'Thailand' },
    { code: '+62', country: 'Indonesia' },
    { code: '+63', country: 'Philippines' },
    { code: '+84', country: 'Vietnam' },
    { code: '+61', country: 'Australia' },
    { code: '+64', country: 'New Zealand' },
    { code: '+49', country: 'Germany' },
    { code: '+33', country: 'France' },
    { code: '+39', country: 'Italy' },
    { code: '+34', country: 'Spain' },
    { code: '+31', country: 'Netherlands' },
    { code: '+32', country: 'Belgium' },
    { code: '+41', country: 'Switzerland' },
    { code: '+43', country: 'Austria' },
    { code: '+45', country: 'Denmark' },
    { code: '+46', country: 'Sweden' },
    { code: '+47', country: 'Norway' },
    { code: '+358', country: 'Finland' },
    { code: '+7', country: 'Russia' },
    { code: '+55', country: 'Brazil' },
    { code: '+52', country: 'Mexico' },
    { code: '+54', country: 'Argentina' },
    { code: '+56', country: 'Chile' },
    { code: '+57', country: 'Colombia' },
    { code: '+51', country: 'Peru' },
    { code: '+58', country: 'Venezuela' },
    { code: '+27', country: 'South Africa' },
    { code: '+20', country: 'Egypt' },
    { code: '+234', country: 'Nigeria' },
    { code: '+254', country: 'Kenya' },
    { code: '+971', country: 'UAE' },
    { code: '+966', country: 'Saudi Arabia' },
    { code: '+974', country: 'Qatar' },
    { code: '+965', country: 'Kuwait' },
    { code: '+973', country: 'Bahrain' },
    { code: '+968', country: 'Oman' },
    { code: '+972', country: 'Israel' },
    { code: '+90', country: 'Turkey' },
    { code: '+98', country: 'Iran' },
    { code: '+92', country: 'Pakistan' },
    { code: '+880', country: 'Bangladesh' },
    { code: '+94', country: 'Sri Lanka' },
    { code: '+977', country: 'Nepal' },
    { code: '+975', country: 'Bhutan' },
    { code: '+93', country: 'Afghanistan' },
    { code: '+998', country: 'Uzbekistan' },
    { code: '+7', country: 'Kazakhstan' },
    { code: '+996', country: 'Kyrgyzstan' },
    { code: '+992', country: 'Tajikistan' },
    { code: '+993', country: 'Turkmenistan' },
    { code: '+374', country: 'Armenia' },
    { code: '+995', country: 'Georgia' },
    { code: '+994', country: 'Azerbaijan' }
  ];

  // Comprehensive list of countries for nationality
  const countries = [
    'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina', 'Armenia', 'Australia', 'Austria',
    'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan',
    'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cambodia', 'Cameroon',
    'Canada', 'Cape Verde', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica',
    'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador', 'Egypt',
    'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon', 'Gambia',
    'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti',
    'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy',
    'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia',
    'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Madagascar', 'Malawi', 'Malaysia',
    'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco',
    'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand',
    'Nicaragua', 'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway', 'Oman', 'Pakistan', 'Palau', 'Panama',
    'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia', 'Rwanda',
    'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles',
    'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa', 'South Korea', 'South Sudan', 'Spain',
    'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand',
    'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda', 'Ukraine',
    'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam', 'Yemen',
    'Zambia', 'Zimbabwe'
  ];

  // Filter active tours for dropdown - handle both regular and custom tours
  const activeTours = tourType === 'custom' 
    ? customTours
        .filter(customTour => selectedTourId ? customTour.id === selectedTourId : true)
        .map(customTour => ({
          tourId: customTour.id,
          tourName: `${customTour.country} Custom Tour`,
          tagline: '',
          destination: customTour.country,
          countries: [customTour.country],
          departureDate: new Date().toISOString(), // Placeholder for custom tours
          status: 'active' as const
        }))
    : tours.filter(tour => tour.status === 'active');
  
  // Debug logging
  console.log('Tour type:', tourType);
  console.log('All tours loaded:', tours.length);
  console.log('Custom tours loaded:', customTours.length);
  console.log('Active tours filtered:', activeTours.length);
  console.log('Tour statuses:', tours.map(t => ({ tourId: t.tourId, status: t.status })));

  const validateField = (name: string, value: any): string => {
    switch (name) {
      case 'givenName':
      case 'surname':
        return !value ? `${name === 'givenName' ? 'Given' : 'Surname'} name is required` : '';
      case 'phoneNumber':
        return !value ? 'Phone number is required' : 
               value.length < 7 ? 'Phone number must be at least 7 digits' : '';
      case 'email':
        return value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? 'Valid email is required' : '';
      case 'dateOfBirth':
        return !value ? 'Date of birth is required' : '';
      case 'passportNumber':
        return !value ? 'Passport number is required' : '';
      case 'passportIssueDate':
        return !value ? 'Passport issue date is required' : '';
      case 'sex':
        return !value ? 'Sex is required' : '';
      case 'nationality':
        return !value ? 'Nationality is required' : '';
      case 'tourId':
        return !value ? 'Please select a tour' : '';
      default:
        return '';
    }
  };

  const validatePassportExpiry = (expiryDate: string, tourId: string): string => {
    if (!expiryDate || !tourId) return '';
    
    const selectedTour = tourType === 'custom' 
      ? customTours.find(tour => tour.id === tourId)
      : tours.find(tour => tour.tourId === tourId);
    if (!selectedTour) return '';
    
    const expiry = new Date(expiryDate);
    // For custom tours, use current date + 6 months as departure date for validation
    const departure = tourType === 'custom' 
      ? new Date(Date.now() + 6 * 30 * 24 * 60 * 60 * 1000) // 6 months from now
      : new Date((selectedTour as Tour).departureDate);
    
    // Calculate 7 months after departure date
    const sevenMonthsAfter = new Date(departure);
    sevenMonthsAfter.setMonth(departure.getMonth() + 7);
    
    if (expiry <= sevenMonthsAfter) {
      return 'Passport must be valid for at least 7 months from the departure date. Please contact the organizer for assistance.';
    }
    
    return '';
  };

  const handleInputChange = (name: string, value: any) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    // Validate passport expiry when passport expiry date is entered
    if (name === 'passportExpiryDate' && value) {
      const passportError = validatePassportExpiry(value, formData.tourId);
      if (passportError) {
        setErrors(prev => ({ ...prev, passportExpiryDate: passportError }));
      }
    }
    
    // Also validate passport expiry when tour is changed and passport expiry date exists
    if (name === 'tourId' && formData.passportExpiryDate) {
      const passportError = validatePassportExpiry(formData.passportExpiryDate, value);
      if (passportError) {
        setErrors(prev => ({ ...prev, passportExpiryDate: passportError }));
      } else {
        // Clear passport expiry error if validation passes
        setErrors(prev => ({ ...prev, passportExpiryDate: '' }));
      }
    }
  };

  const validateForm = (): boolean => {
    const requiredFields = ['givenName', 'surname', 'phoneNumber', 'dateOfBirth', 'passportNumber', 'passportIssueDate', 'tourId', 'sex', 'nationality'];
    const newErrors: Record<string, string> = {};

    requiredFields.forEach(field => {
      const error = validateField(field, formData[field as keyof RegistrationFormData]);
      if (error) {
        newErrors[field] = error;
      }
    });

    // Validate email format only if provided (email is optional)
    const emailError = validateField('email', formData.email);
    if (emailError) {
      newErrors.email = emailError;
    }

    // Validate passport expiry if provided - this is a blocking validation
    if (formData.passportExpiryDate) {
      const passportError = validatePassportExpiry(formData.passportExpiryDate, formData.tourId);
      if (passportError) {
        newErrors.passportExpiryDate = passportError;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Convert date fields to ISO datetime strings for backend and clean up empty optional fields
      const submissionData = { ...formData };
      
      // Combine country code with phone number
      submissionData.phoneNumber = `${countryCode}${formData.phoneNumber}`;
      
      // Handle required date fields
      if (submissionData.dateOfBirth) {
        submissionData.dateOfBirth = new Date(submissionData.dateOfBirth + 'T00:00:00.000Z').toISOString();
      }
      
      if (submissionData.passportIssueDate) {
        submissionData.passportIssueDate = new Date(submissionData.passportIssueDate + 'T00:00:00.000Z').toISOString();
      }
      
      // Handle optional date field - only include if not empty
      if (submissionData.passportExpiryDate && submissionData.passportExpiryDate.trim()) {
        submissionData.passportExpiryDate = new Date(submissionData.passportExpiryDate + 'T00:00:00.000Z').toISOString();
      } else {
        delete submissionData.passportExpiryDate; // Remove empty optional field
      }
      
      // Combine address fields into single address field
      const addressParts = [];
      if (submissionData.addressLine1) addressParts.push(submissionData.addressLine1);
      if (submissionData.addressLine2) addressParts.push(submissionData.addressLine2);
      if (submissionData.city) addressParts.push(submissionData.city);
      if (submissionData.state) addressParts.push(submissionData.state);
      if (submissionData.country) addressParts.push(submissionData.country);
      if (submissionData.zipCode) addressParts.push(submissionData.zipCode);
      
      if (addressParts.length > 0) {
        submissionData.address = addressParts.join(', ');
      }
      
      // Remove the individual address fields as they're not needed in the backend
      delete submissionData.addressLine1;
      delete submissionData.addressLine2;
      delete submissionData.city;
      delete submissionData.state;
      delete submissionData.country;
      delete submissionData.zipCode;
      
      // Combine emergency contact fields into single emergency contact field
      const emergencyContactParts = [];
      if (submissionData.emergencyContactName) emergencyContactParts.push(submissionData.emergencyContactName);
      if (submissionData.emergencyContactPhone) emergencyContactParts.push(submissionData.emergencyContactPhone);
      
      if (emergencyContactParts.length > 0) {
        submissionData.emergencyContact = emergencyContactParts.join(' - ');
      }
      
      // Remove the individual emergency contact fields as they're not needed in the backend
      delete submissionData.emergencyContactName;
      delete submissionData.emergencyContactPhone;
      
      // Clean up empty optional string fields
      const optionalFields = ['email', 'address', 'emergencyContact', 'dietaryRequirements', 'medicalConditions'];
      optionalFields.forEach(field => {
        if (submissionData[field as keyof typeof submissionData] === '') {
          delete submissionData[field as keyof typeof submissionData];
        }
      });
      
      console.log('Submitting form data:', submissionData);
      console.log('Selected tour ID:', submissionData.tourId);
      console.log('Available tours:', activeTours.map(t => ({ tourId: t.tourId, tourName: t.tourName })));
      
      const result = await submitRegistration(submissionData);
      setIsSubmitted(true);
      onSuccess?.(result.id);
    } catch (error: any) {
      console.error('Form submission error:', error);
      onError?.(error.message || 'Failed to submit registration');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-2xl mx-auto p-8 bg-white rounded-2xl shadow-xl border border-neutral-medium-gray"
      >
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
            <CheckCircle size={32} className="text-green-600" />
          </div>
          <h2 className="heading-secondary text-neutral-charcoal mb-4">
            Registration Submitted Successfully!
          </h2>
          <p className="body-regular text-secondary-slate mb-6">
            Thank you for your interest in our tour. We have received your registration and will contact you soon with further details.
          </p>
          <button
            onClick={() => window.location.href = '/'}
            className="btn btn-primary"
          >
            Return to Home
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-4xl mx-auto"
    >
      <div className="bg-white rounded-2xl shadow-xl border border-neutral-medium-gray overflow-hidden">
        {/* Header */}
        <div className="gradient-primary p-8 text-white">
          <h1 className="heading-primary mb-2">Tour Registration</h1>
          <p className="body-regular opacity-90">
            Fill out the form below to register for your dream tour
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-8 space-y-8">
          {/* Tour Selection */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-primary-royal-blue bg-opacity-10 rounded-lg">
                <Users size={20} className="text-primary-royal-blue" />
              </div>
              <h2 className="heading-tertiary text-neutral-charcoal">Tour Selection</h2>
            </div>

            <div>
              <label className="block body-small font-medium text-neutral-dark-gray mb-2">
                Selected Tour <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.tourId}
                onChange={(e) => handleInputChange('tourId', e.target.value)}
                disabled={true}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-deep-blue ${
                  errors.tourId ? 'border-red-500' : 'border-neutral-medium-gray'
                } bg-neutral-light-gray cursor-not-allowed opacity-75`}
              >
                <option value="">Tour Selected</option>
                {activeTours.map((tour) => (
                  <option key={tour.tourId} value={tour.tourId}>
                    {tour.tourName} - {tour.destination} {tourType === 'custom' ? '(Custom)' : `(${new Date(tour.departureDate).toLocaleDateString()})`}
                  </option>
                ))}
              </select>
              <p className="text-sm text-neutral-dark-gray mt-2 flex items-center gap-1">
                <CheckCircle size={14} className="text-green-600" />
                {tourType === 'custom' ? 'Custom tour selected from previous page' : 'Tour selected from tours page'}
              </p>
              {errors.tourId && (
                <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle size={14} />
                  {errors.tourId}
                </p>
              )}
            </div>
          </div>

          {/* Personal Information */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-primary-deep-blue bg-opacity-10 rounded-lg">
                <User size={20} className="text-primary-deep-blue" />
              </div>
              <h2 className="heading-tertiary text-neutral-charcoal">Personal Information</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block body-small font-medium text-neutral-dark-gray mb-2">
                  Given Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.givenName}
                  onChange={(e) => handleInputChange('givenName', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-deep-blue ${
                    errors.givenName ? 'border-red-500' : 'border-neutral-medium-gray'
                  }`}
                  placeholder="Enter your given name"
                />
                {errors.givenName && (
                  <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.givenName}
                  </p>
                )}
              </div>

              <div>
                <label className="block body-small font-medium text-neutral-dark-gray mb-2">
                  Surname <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.surname}
                  onChange={(e) => handleInputChange('surname', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-deep-blue ${
                    errors.surname ? 'border-red-500' : 'border-neutral-medium-gray'
                  }`}
                  placeholder="Enter your surname"
                />
                {errors.surname && (
                  <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.surname}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block body-small font-medium text-neutral-dark-gray mb-2">
                  <Phone size={16} className="inline mr-2" />
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    className="px-3 py-3 border border-neutral-medium-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-deep-blue bg-white min-w-[120px]"
                  >
                    {countryCodes.map((country, index) => (
                      <option key={`${country.code}-${country.country}-${index}`} value={country.code}>
                        {country.code} {country.country}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                    className={`flex-1 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-deep-blue ${
                      errors.phoneNumber ? 'border-red-500' : 'border-neutral-medium-gray'
                    }`}
                    placeholder="Enter your phone number"
                  />
                </div>
                {errors.phoneNumber && (
                  <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.phoneNumber}
                  </p>
                )}
              </div>

              <div>
                <label className="block body-small font-medium text-neutral-dark-gray mb-2">
                  <Mail size={16} className="inline mr-2" />
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-deep-blue ${
                    errors.email ? 'border-red-500' : 'border-neutral-medium-gray'
                  }`}
                  placeholder="Enter your email address"
                />
                {errors.email && (
                  <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.email}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block body-small font-medium text-neutral-dark-gray mb-2">
                  <Calendar size={16} className="inline mr-2" />
                  Date of Birth <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-deep-blue ${
                    errors.dateOfBirth ? 'border-red-500' : 'border-neutral-medium-gray'
                  }`}
                />
                {errors.dateOfBirth && (
                  <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.dateOfBirth}
                  </p>
                )}
              </div>

              <div>
                <label className="block body-small font-medium text-neutral-dark-gray mb-2">
                  Sex <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.sex}
                  onChange={(e) => handleInputChange('sex', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-deep-blue ${
                    errors.sex ? 'border-red-500' : 'border-neutral-medium-gray'
                  }`}
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
                {errors.sex && (
                  <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.sex}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Passport Information */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-accent-rose-gold bg-opacity-10 rounded-lg">
                <FileText size={20} className="text-accent-rose-gold" />
              </div>
              <h2 className="heading-tertiary text-neutral-charcoal">Passport Information</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block body-small font-medium text-neutral-dark-gray mb-2">
                  Passport Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.passportNumber}
                  onChange={(e) => handleInputChange('passportNumber', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-deep-blue ${
                    errors.passportNumber ? 'border-red-500' : 'border-neutral-medium-gray'
                  }`}
                  placeholder="Enter your passport number"
                />
                {errors.passportNumber && (
                  <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.passportNumber}
                  </p>
                )}
              </div>

              <div>
                <label className="block body-small font-medium text-neutral-dark-gray mb-2">
                  Nationality <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.nationality}
                  onChange={(e) => handleInputChange('nationality', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-deep-blue ${
                    errors.nationality ? 'border-red-500' : 'border-neutral-medium-gray'
                  }`}
                >
                  {countries.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
                {errors.nationality && (
                  <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.nationality}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block body-small font-medium text-neutral-dark-gray mb-2">
                  Passport Date of Issue <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.passportIssueDate}
                  onChange={(e) => handleInputChange('passportIssueDate', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-deep-blue ${
                    errors.passportIssueDate ? 'border-red-500' : 'border-neutral-medium-gray'
                  }`}
                />
                {errors.passportIssueDate && (
                  <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.passportIssueDate}
                  </p>
                )}
              </div>

              <div>
                <label className="block body-small font-medium text-neutral-dark-gray mb-2">
                  Passport Expiry Date
                </label>
                <input
                  type="date"
                  value={formData.passportExpiryDate || ''}
                  onChange={(e) => handleInputChange('passportExpiryDate', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-deep-blue ${
                    errors.passportExpiryDate ? 'border-red-500' : 'border-neutral-medium-gray'
                  }`}
                />
                {errors.passportExpiryDate && (
                  <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.passportExpiryDate}
                  </p>
                )}
              </div>

              <div>
                <label className="block body-small font-medium text-neutral-dark-gray mb-2">
                  <MapPin size={16} className="inline mr-2" />
                  Address Line 1
                </label>
                <input
                  type="text"
                  value={formData.addressLine1 || ''}
                  onChange={(e) => handleInputChange('addressLine1', e.target.value)}
                  className="w-full px-4 py-3 border border-neutral-medium-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-deep-blue"
                  placeholder="Street address"
                />
              </div>

              <div>
                <label className="block body-small font-medium text-neutral-dark-gray mb-2">
                  Address Line 2
                </label>
                <input
                  type="text"
                  value={formData.addressLine2 || ''}
                  onChange={(e) => handleInputChange('addressLine2', e.target.value)}
                  className="w-full px-4 py-3 border border-neutral-medium-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-deep-blue"
                  placeholder="Apartment, suite, unit, etc. (optional)"
                />
              </div>

              <div>
                <label className="block body-small font-medium text-neutral-dark-gray mb-2">
                  City
                </label>
                <input
                  type="text"
                  value={formData.city || ''}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  className="w-full px-4 py-3 border border-neutral-medium-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-deep-blue"
                  placeholder="City"
                />
              </div>

              <div>
                <label className="block body-small font-medium text-neutral-dark-gray mb-2">
                  State/Province
                </label>
                <input
                  type="text"
                  value={formData.state || ''}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  className="w-full px-4 py-3 border border-neutral-medium-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-deep-blue"
                  placeholder="State or Province"
                />
              </div>

              <div>
                <label className="block body-small font-medium text-neutral-dark-gray mb-2">
                  Country
                </label>
                <input
                  type="text"
                  value={formData.country || ''}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  className="w-full px-4 py-3 border border-neutral-medium-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-deep-blue"
                  placeholder="Country"
                />
              </div>

              <div>
                <label className="block body-small font-medium text-neutral-dark-gray mb-2">
                  ZIP/Postal Code
                </label>
                <input
                  type="text"
                  value={formData.zipCode || ''}
                  onChange={(e) => handleInputChange('zipCode', e.target.value)}
                  className="w-full px-4 py-3 border border-neutral-medium-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-deep-blue"
                  placeholder="ZIP or Postal Code"
                />
              </div>
            </div>
          </div>


          {/* Additional Information */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-accent-copper bg-opacity-10 rounded-lg">
                <AlertCircle size={20} className="text-accent-copper" />
              </div>
              <h2 className="heading-tertiary text-neutral-charcoal">Additional Information</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block body-small font-medium text-neutral-dark-gray mb-2">
                  Emergency Contact Name
                </label>
                <input
                  type="text"
                  value={formData.emergencyContactName || ''}
                  onChange={(e) => handleInputChange('emergencyContactName', e.target.value)}
                  className="w-full px-4 py-3 border border-neutral-medium-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-deep-blue"
                  placeholder="Emergency contact name"
                />
              </div>

              <div>
                <label className="block body-small font-medium text-neutral-dark-gray mb-2">
                  Emergency Contact Phone
                </label>
                <input
                  type="tel"
                  value={formData.emergencyContactPhone || ''}
                  onChange={(e) => handleInputChange('emergencyContactPhone', e.target.value)}
                  className="w-full px-4 py-3 border border-neutral-medium-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-deep-blue"
                  placeholder="Emergency contact phone number"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block body-small font-medium text-neutral-dark-gray mb-2">
                  Dietary Requirements
                </label>
                <select
                  value={formData.dietaryRequirements || ''}
                  onChange={(e) => handleInputChange('dietaryRequirements', e.target.value)}
                  className="w-full px-4 py-3 border border-neutral-medium-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-deep-blue"
                >
                  <option value="">Select dietary preference</option>
                  <option value="veg">Vegetarian</option>
                  <option value="non-veg">Non-Vegetarian</option>
                </select>
              </div>

              <div>
                <label className="block body-small font-medium text-neutral-dark-gray mb-2">
                  Medical Conditions
                </label>
                <textarea
                  value={formData.medicalConditions || ''}
                  onChange={(e) => handleInputChange('medicalConditions', e.target.value)}
                  className="w-full px-4 py-3 border border-neutral-medium-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-deep-blue"
                  placeholder="Any medical conditions we should know about"
                  rows={3}
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-6 border-t border-neutral-medium-gray">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full btn btn-primary btn-lg ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Registration'}
            </button>
          </div>
        </form>
      </div>
    </motion.div>
  );
};

export default RegistrationForm;
